import { Label } from '@/components/ui/Label'
import { TagInput } from '@/components/ui/TagInput'
import { SearchSelect } from '@/components/ui/SearchSelect'
import { cn } from '@/lib/utils'
import type { HelpBannerScope } from '@/pages/HelpCenter/types'

interface BannerTargetingProps {
  scope: HelpBannerScope
  tags: string[]
  categoryId: string | null
  categories: { id: string; name: string }[]
  availableTags: string[]
  onScopeChange: (scope: HelpBannerScope) => void
  onTagsChange: (tags: string[]) => void
  onCategoryChange: (id: string) => void
}

const SCOPES: { value: HelpBannerScope; label: string; hint: string }[] = [
  { value: 'all', label: 'Whole help center', hint: 'Every article and category page.' },
  { value: 'tagged', label: 'Articles with a tag', hint: 'Any article carrying one of the tags below.' },
  { value: 'category', label: 'One category', hint: 'That category page and the articles in it.' },
]

export function BannerTargeting({
  scope, tags, categoryId, categories, availableTags,
  onScopeChange, onTagsChange, onCategoryChange,
}: BannerTargetingProps) {
  // Tags are free text on articles, so a banner pointed at a tag nobody has
  // spelled that way matches nothing and says nothing about why. The type-ahead
  // makes picking an existing tag the easy path, and flagging the ones that
  // match no article catches a typo when it happens anyway.
  const unused = tags.filter(t => !availableTags.includes(t))

  return (
    <div className="space-y-3">
      <Label>Show on</Label>
      <div className="grid gap-2">
        {SCOPES.map(({ value, label, hint }) => (
          <button
            key={value}
            type="button"
            onClick={() => onScopeChange(value)}
            aria-pressed={scope === value}
            className={cn(
              'cursor-pointer rounded-md border px-3 py-2 text-left transition-colors',
              scope === value ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/40',
            )}
          >
            <span className="block text-sm font-medium">{label}</span>
            <span className="block text-xs text-muted-foreground">{hint}</span>
          </button>
        ))}
      </div>

      {scope === 'tagged' && (
        <div className="space-y-2">
          <TagInput
            value={tags}
            onChange={onTagsChange}
            suggestions={availableTags}
            placeholder="legacy-v1, deprecated…"
          />
          <p className="text-xs text-muted-foreground">
            {availableTags.length > 0
              ? 'Start typing to pick from the tags already on your articles, or enter a new one.'
              : 'No articles are tagged yet. Add tags to articles first, then point a banner at them.'}
          </p>
          {unused.length > 0 && (
            <p className="text-xs text-amber-500">
              No article is tagged {unused.map(t => `"${t}"`).join(', ')} yet, so nothing will show this banner.
            </p>
          )}
        </div>
      )}

      {scope === 'category' && (
        <SearchSelect
          value={categoryId}
          options={categories.map(c => ({ value: c.id, label: c.name }))}
          onChange={onCategoryChange}
          placeholder="Pick a category"
          searchPlaceholder="Search categories…"
          emptyText="No matching category."
          ariaLabel="Category"
        />
      )}
    </div>
  )
}
